import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "@/hooks/useTranslation";
import { getReadableErrorMessage } from "@/utils/errorHandler";

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL } from "@/utils/apiConfig";
import { getLoginPath, getHomePath, getTermsPath, getPrivacyPath } from "@/utils/localePaths";

function GoogleCompleteRegistration() {
  const { t, locale } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [profileType, setProfileType] = useState(""); // No default - user must choose
  const [sessionToken, setSessionToken] = useState(null);
  const { setUser, setToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Get session parameter from URL query (native OAuth)
    const params = new URLSearchParams(window.location.search);
    const session_id = params.get('session');

    if (!session_id) {
      toast.error(t('auth.google.invalid_session'));
      navigate(getLoginPath(locale));
      return;
    }

    // Session ID is valid, just store it and show the form
    setSessionToken(session_id);
    setLoading(false);
  }, [navigate]);

  const handleComplete = async (e) => {
    e.preventDefault();
    
    if (!profileType) {
      toast.error(t('messages.select_profile'));
      return;
    }
    
    if (!termsAccepted) {
      toast.error(t('auth.google.must_accept_terms'));
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/google/complete-native`, {
        session_token: sessionToken,
        terms_accepted: termsAccepted,
        profile_type: profileType
      });

      const { access_token, user } = response.data;

      // Store auth data
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setToken(access_token);
      setUser(user);

      toast.success(t('messages.registration_welcome'));
      navigate(getHomePath(locale));
    } catch (error) {
      console.error("Registration completion error:", error);
      toast.error(getReadableErrorMessage(error, t, 'auth.google.complete_error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#FFDD59] mx-auto mb-4" />
          <p className="text-gray-400">{t('auth.google.processing')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-900 border-2 border-[#FFDD59]">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle2 className="w-16 h-16 text-green-500" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#FFDD59]">
            {t('auth.google.complete_title')}
          </CardTitle>
          <CardDescription className="text-gray-300 mt-2 text-base">
            {t('auth.google.last_step')}
            <br />
            {t('auth.google.fill_below')}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleComplete} className="space-y-6">
            {/* Profile Type - MANDATORY */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                {t('auth.register.profile_question')} <span className="text-red-400">*</span>
              </label>
              <div className="space-y-2">
                <label className={`flex items-center p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 border-2 ${profileType === "company" ? "border-[#FFDD59]" : "border-transparent"}`}>
                  <input
                    type="radio"
                    name="profileType"
                    value="company"
                    checked={profileType === "company"}
                    onChange={(e) => setProfileType(e.target.value)}
                    className="mr-3 text-[#FFDD59] focus:ring-[#FFDD59]"
                  />
                  <span className="text-white">{t('auth.register.company_user')}</span>
                </label>
                <label className={`flex items-center p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 border-2 ${profileType === "visitor" ? "border-[#FFDD59]" : "border-transparent"}`}>
                  <input
                    type="radio"
                    name="profileType"
                    value="visitor"
                    checked={profileType === "visitor"}
                    onChange={(e) => setProfileType(e.target.value)}
                    className="mr-3 text-[#FFDD59] focus:ring-[#FFDD59]"
                  />
                  <span className="text-white">{t('auth.register.individual_user')}</span>
                </label>
              </div>
            </div>

            {/* Terms Acceptance - MANDATORY */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  required
                  className="mt-1 mr-3 w-4 h-4 rounded text-[#FFDD59] focus:ring-[#FFDD59]"
                />
                <span className="text-sm text-gray-300">
                  {t('auth.google.terms_consent').split(/(\{TERMS\}|\{KVKK\})/).map((seg, i) => {
                    if (seg === '{TERMS}') return <Link key={i} to={getTermsPath(locale)} className="text-[#FFDD59] hover:underline">{t('auth.register.terms_link')}</Link>;
                    if (seg === '{KVKK}') return <Link key={i} to={getPrivacyPath(locale)} className="text-[#FFDD59] hover:underline">{t('auth.google.kvkk_link')}</Link>;
                    return seg;
                  })}{" "}
                  <span className="text-red-400">*</span>
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting || !termsAccepted || !profileType}
              className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold text-base py-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t('auth.creating_account')}
                </>
              ) : (
                t('auth.google.create_btn')
              )}
            </Button>

            {/* Info */}
            <p className="text-xs text-white text-center mt-4">
              {t('auth.google.free_credit_note')}
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default GoogleCompleteRegistration;
